import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

const LinkPreview = ({ url }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isPlayingYoutube, setIsPlayingYoutube] = useState(false);

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const youtubeId = getYouTubeId(url);

  useEffect(() => {
    if (!url || youtubeId) {
      setLoading(false);
      return;
    }
    fetch(`https://api.microlink.io?url=${encodeURIComponent(url)}`)
      .then(res => res.json())
      .then(json => {
        if (json.status === 'success' && json.data) {
          setData(json.data);
        } else {
          setError(true);
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [url, youtubeId]);

  if (!url || url.startsWith('urn:')) {
    return null;
  }

  if (youtubeId) {
    return (
      <Box
        sx={{
          display: 'block',
          border: '1px solid #E5E7EB',
          borderRadius: '10px',
          overflow: 'hidden',
          backgroundColor: '#fff',
          width: '100%',
          pt: '56.25%', // 16:9 aspect ratio
          position: 'relative'
        }}
        onClick={(e) => {
          e.stopPropagation();
          if (!isPlayingYoutube) setIsPlayingYoutube(true);
        }}
      >
        {isPlayingYoutube ? (
          <iframe
            src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0
            }}
          />
        ) : (
          <>
            <img 
              src={`https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`} 
              alt="YouTube Thumbnail" 
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }} 
            />
            <Box 
              sx={{ 
                position: 'absolute', 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)', 
                backgroundColor: 'rgba(0,0,0,0.6)', 
                color: '#fff', 
                borderRadius: '50%', 
                padding: '12px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
              }}
            >
               <PlayArrowIcon sx={{ fontSize: 40 }} />
            </Box>
          </>
        )}
      </Box>
    );
  }

  const FallbackUI = () => (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: "10px 14px",
        backgroundColor: "#FFFBEB",
        border: "1px solid #FCD34D",
        borderRadius: "10px",
        textDecoration: "none",
        cursor: "pointer",
        "&:hover": { backgroundColor: "#FEF3C7" },
        width: "100%",
        boxSizing: "border-box"
      }}
    >
      <StarIcon sx={{ color: "#B45309", fontSize: 18, flexShrink: 0 }} />
      <Box sx={{ overflow: "hidden", flex: 1 }}>
        <Typography fontSize="11px" fontWeight={600} color="#92400E" noWrap>
          Featured Article
        </Typography>
        <Typography
          fontSize="11px"
          color="#B45309"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {url}
        </Typography>
      </Box>
      <OpenInNewIcon sx={{ color: "#B45309", fontSize: 14, flexShrink: 0 }} />
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} sx={{ color: "#FCD34D" }} />
      </Box>
    );
  }

  if (error || !data || (!data.image && !data.title)) {
    return <FallbackUI />;
  }

  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: 'block',
        border: '1px solid #E5E7EB',
        borderRadius: '10px',
        overflow: 'hidden',
        textDecoration: 'none',
        color: 'inherit',
        backgroundColor: '#fff',
        '&:hover': {
          backgroundColor: '#F9FAFB',
        },
        width: '100%'
      }}
    >
      {data.image?.url && (
        <Box
          sx={{
            width: '100%',
            height: '150px',
            backgroundImage: `url(${data.image.url})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderBottom: '1px solid #E5E7EB'
          }}
        />
      )}
      <Box sx={{ p: 2 }}>
        <Typography 
          sx={{ 
            fontSize: '12px', 
            fontWeight: 600, 
            color: '#111827', 
            display: '-webkit-box', 
            WebkitLineClamp: 2, 
            WebkitBoxOrient: 'vertical', 
            overflow: 'hidden',
            mb: 0.5
          }}
        >
          {data.title || url}
        </Typography>
        {data.description && (
          <Typography 
            sx={{ 
              fontSize: '12px', 
              color: '#6B7280', 
              display: '-webkit-box', 
              WebkitLineClamp: 2, 
              WebkitBoxOrient: 'vertical', 
              overflow: 'hidden',
              mb: 1
            }}
          >
            {data.description}
          </Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {data.logo?.url && (
            <img src={data.logo.url} alt="Logo" style={{ width: '16px', height: '16px', borderRadius: '2px' }} />
          )}
          <Typography sx={{ fontSize: '11px', color: '#9CA3AF' }}>
            {data.publisher || new URL(url).hostname}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LinkPreview;
